import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";

export type OrientationAction = "lock-portrait" | "unlock";

export function decideOrientation(fullscreenEl: Element | null): OrientationAction {
  return fullscreenEl ? "unlock" : "lock-portrait";
}

export async function applyOrientation(action: OrientationAction): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      if (action === "unlock") {
        await ScreenOrientation.unlock();
      } else {
        await ScreenOrientation.lock({ orientation: "portrait" });
      }
    } catch (e) {
      console.warn("[orientation] native call failed:", action, e);
    }
    return;
  }

  // Web: screen.orientation.lock requires a fullscreen element. When entering
  // fullscreen we can request landscape; on exit we let the browser do its thing.
  const so = typeof screen !== "undefined" ? (screen.orientation as ScreenOrientation | undefined) : undefined;
  if (!so) return;

  if (action === "unlock") {
    try {
      // Best-effort landscape lock while fullscreen is active.
      await (so as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.("landscape");
    } catch {
      // Browser may reject (not fullscreen / unsupported) — silent.
    }
  } else {
    try {
      (so as ScreenOrientation & { unlock?: () => void }).unlock?.();
    } catch {
      // ignore
    }
  }
}

interface InstallOptions {
  doc?: Document;
  apply?: (action: OrientationAction) => void;
}

export function installFullscreenOrientationListener(opts: InstallOptions = {}): () => void {
  const doc = opts.doc ?? (typeof document !== "undefined" ? document : undefined);
  if (!doc) return () => {};
  const apply = opts.apply ?? ((a: OrientationAction) => { void applyOrientation(a); });

  const handler = () => {
    const docWithWebkit = doc as Document & { webkitFullscreenElement?: Element | null };
    const el = doc.fullscreenElement ?? docWithWebkit.webkitFullscreenElement ?? null;
    apply(decideOrientation(el));
  };

  doc.addEventListener("fullscreenchange", handler);
  doc.addEventListener("webkitfullscreenchange", handler);

  return () => {
    doc.removeEventListener("fullscreenchange", handler);
    doc.removeEventListener("webkitfullscreenchange", handler);
  };
}
